import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  FileClock, Eye, Clock, Flame, TrendingUp, Send, BellOff, Bell, Loader2,
  ExternalLink, Copy, DollarSign, XCircle, CheckCircle2, Inbox,
} from "lucide-react";
import {
  fetchProposalAttention, fetchProposalRecovery, sendProposalFollowup,
  logProposalResponse, proposalFollowupControl, proposalLink, money,
} from "@/lib/dashboardApi";

const PERIODS = [
  { value: "week", label: "This Week" },
  { value: "month", label: "Last 30 Days" },
  { value: "ytd", label: "Year to Date" },
  { value: "all", label: "All Time" },
];

const Card = ({ icon: Icon, label, value, accent, active, onClick }) => (
  <button onClick={onClick} data-testid={`prop-card-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    className={`text-left bg-white border rounded-xl p-4 flex items-center gap-3 shadow-sm transition-all ${active ? "border-orange-400 ring-2 ring-orange-200" : "border-slate-200 hover:border-slate-300"}`}>
    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${accent}1f` }}>
      <Icon className="w-5 h-5" style={{ color: accent }} />
    </div>
    <div>
      <div className="text-2xl font-bold text-slate-900 leading-none">{value}</div>
      <div className="text-slate-500 text-xs mt-1">{label}</div>
    </div>
  </button>
);

function List({ items, onRefresh }) {
  const [busy, setBusy] = useState(null);
  if (!items?.length) return <p className="text-slate-400 text-sm py-8 text-center">Nothing here right now 🎉</p>;
  const act = async (id, fn, msg) => {
    setBusy(id);
    try { await fn(); toast.success(msg); onRefresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(null); }
  };
  const copy = (p) => { navigator.clipboard.writeText(proposalLink(p)); toast.success("Customer link copied"); };
  return (
    <div className="divide-y divide-slate-100">
      {items.map((p) => (
        <div key={p.id} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`prop-row-${p.id}`}>
          <div className="min-w-0">
            <p className="text-slate-900 font-medium truncate">
              {p.customer_name || "Unnamed"} <span className="text-emerald-600 font-semibold">· {money(p.value)}</span>
            </p>
            <p className="text-slate-500 text-xs truncate">
              {p.title || "Proposal"}
              {p.customer_viewed_at ? " · 👀 viewed" : " · not opened"}
              {p.follow_up_count ? ` · ${p.follow_up_count} follow-up(s)` : ""}
              {p.follow_up_stopped ? ` · stopped (${(p.follow_up_stop_reason || "").replace("_", " ")})` : (p.next_follow_up_at ? ` · next ${new Date(p.next_follow_up_at).toLocaleDateString()}` : "")}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <a href={proposalLink(p)} target="_blank" rel="noreferrer" className="p-2 rounded-md text-slate-500 hover:text-orange-600 hover:bg-orange-50" title="Open customer view"><ExternalLink className="w-4 h-4" /></a>
            <button onClick={() => copy(p)} className="p-2 rounded-md text-slate-500 hover:text-orange-600 hover:bg-orange-50" title="Copy link"><Copy className="w-4 h-4" /></button>
            <button disabled={busy === p.id} onClick={() => act(p.id, () => sendProposalFollowup(p.id), "Follow-up sent")} data-testid={`prop-followup-${p.id}`}
              className="text-xs font-semibold px-2.5 py-1.5 rounded-md bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-1">
              {busy === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Follow up
            </button>
            <button disabled={busy === p.id} onClick={() => act(p.id, () => logProposalResponse(p.id, { channel: "note" }), "Marked as responded")} data-testid={`prop-responded-${p.id}`}
              className="text-xs font-semibold px-2.5 py-1.5 rounded-md border border-slate-300 text-slate-600 hover:border-emerald-400 hover:text-emerald-600">Responded</button>
            {p.follow_up_stopped ? (
              <button disabled={busy === p.id} onClick={() => act(p.id, () => proposalFollowupControl(p.id, "resume"), "Follow-up resumed")} data-testid={`prop-resume-${p.id}`}
                className="text-xs font-semibold px-2 py-1.5 rounded-md text-slate-500 hover:text-slate-800 hover:bg-slate-100 inline-flex items-center gap-1"><Bell className="w-3.5 h-3.5" /></button>
            ) : (
              <button disabled={busy === p.id} onClick={() => act(p.id, () => proposalFollowupControl(p.id, "stop"), "Follow-up stopped")} data-testid={`prop-stop-${p.id}`}
                className="text-xs font-semibold px-2 py-1.5 rounded-md text-slate-500 hover:text-slate-800 hover:bg-slate-100 inline-flex items-center gap-1"><BellOff className="w-3.5 h-3.5" /></button>
            )}
            <button disabled={busy === p.id} onClick={() => act(p.id, () => proposalFollowupControl(p.id, "declined"), "Marked declined")} data-testid={`prop-declined-${p.id}`}
              className="text-xs font-semibold px-2 py-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 inline-flex items-center gap-1" title="Mark declined / lost"><XCircle className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ProposalFollowup() {
  const [tab, setTab] = useState("all_open");
  const [period, setPeriod] = useState("month");
  const { data: attn, refetch: refetchAttn } = useQuery({ queryKey: ["prop-attention"], queryFn: fetchProposalAttention });
  const { data: rec, refetch: refetchRec } = useQuery({ queryKey: ["prop-recovery", period], queryFn: () => fetchProposalRecovery(period) });
  const refresh = () => { refetchAttn(); refetchRec(); };

  const c = attn?.counts || {};
  const TABS = [
    { key: "all_open", label: "All Open", icon: Inbox, accent: "#12355B", count: c.open },
    { key: "follow_up_due", label: "Follow-Up Due", icon: Clock, accent: "#f59e0b", count: c.follow_up_due },
    { key: "viewed_no_action", label: "Viewed, No Action", icon: Eye, accent: "#0ea5e9", count: c.viewed_no_action },
    { key: "no_decision", label: "Sent, Not Opened", icon: FileClock, accent: "#a855f7", count: c.no_decision },
    { key: "going_stale", label: "Going Stale (14d+)", icon: Clock, accent: "#ef4444", count: c.going_stale },
    { key: "high_value", label: "High-Value", icon: Flame, accent: "#F97316", count: c.high_value },
  ];

  return (
    <div className="space-y-6" data-testid="proposal-followup">
      {/* Recovery + open pipeline summary */}
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4" data-testid="prop-recovered-stat">
          <div className="flex items-center gap-2 text-emerald-700 text-xs font-semibold uppercase tracking-wide"><DollarSign className="w-4 h-4" /> Revenue Recovered</div>
          <p className="text-2xl font-extrabold text-emerald-700 mt-1">{money(rec?.recovered_revenue || 0)}</p>
          <p className="text-emerald-600 text-xs">{rec?.recovered_count || 0} unsold proposal(s) closed after follow-up</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wide"><TrendingUp className="w-4 h-4" /> Open Pipeline</div>
          <p className="text-2xl font-extrabold text-navy mt-1">{money(attn?.open_value || 0)}</p>
          <p className="text-slate-500 text-xs">{c.open || 0} sent-but-unsold proposal(s)</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between">
          <div>
            <div className="text-slate-500 text-xs font-semibold uppercase tracking-wide">Recovery Period</div>
            <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="prop-recovery-period"
              className="mt-2 bg-white border border-slate-300 rounded-md px-3 py-1.5 text-sm text-slate-700">
              {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <CheckCircle2 className="w-8 h-8 text-emerald-200" />
        </div>
      </div>

      {/* Attention buckets */}
      <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-2xl p-5 shadow-sm">
        <h2 className="text-lg font-bold text-navy flex items-center gap-2 mb-4"><FileClock className="w-5 h-5 text-orange-500" /> Unsold Proposals to Follow Up</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
          {TABS.map((t) => (
            <Card key={t.key} icon={t.icon} label={t.label} value={t.count ?? 0} accent={t.accent} active={tab === t.key} onClick={() => setTab(t.key)} />
          ))}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <List items={attn?.[tab] || []} onRefresh={refresh} />
        </div>
        <p className="text-xs text-slate-400 mt-3">Lists are prioritized by dollar value, then age. Automated follow-up stops the moment a customer replies, signs, declines, or is marked do-not-contact. No internal cost or margin is ever shown here.</p>
      </div>

      {/* Recovered proposals detail */}
      {rec?.proposals?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid="prop-recovery-list">
          <h3 className="text-base font-bold text-navy mb-3">Closed After Follow-Up</h3>
          <div className="divide-y divide-slate-100">
            {rec.proposals.map((p) => (
              <div key={p.id} className="py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-slate-900 font-medium truncate">{p.customer_name}</p>
                  <p className="text-slate-500 text-xs truncate">{p.title || "Proposal"} · {p.follow_up_count} follow-up(s){p.salesperson ? ` · ${p.salesperson}` : ""}</p>
                </div>
                <span className="text-emerald-700 font-bold shrink-0">{money(p.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
